<script lang="ts">
  import {
    AlertCircle,
    ArrowRight,
    CheckCircle2,
    Circle,
    Cpu,
    Download,
    Gamepad2,
    Layers,
    Lightbulb,
    Package,
    Route
  } from 'lucide-svelte';
  import { router } from '$lib/router';
  import { unitsQuery } from '../queries/units';
  import { zonesQuery } from '../queries/zones';
  import { roomsQuery } from '../queries/rooms';
  import { loadsQuery } from '../queries/loads';
  import { inputDevicesQuery } from '../queries/input_devices';
  import { rcusQuery } from '../queries/rcus';
  import { dinModulesForUnitQuery } from '../queries/din_modules';
  import { rcuChannelsQuery } from '../queries/rcu_channels';
  import { scenesQuery } from '../queries/scenes';
  import { unitSceneLoadsQuery } from '../queries/scene_loads';
  import { productsQuery } from '../queries/products';
  import { computeUnitWorkflow } from '../workflow';

  let {
    onnavigate = (_detail: { section: string }) => {}
  }: { onnavigate?: (detail: { section: string }) => void } = $props();
  const stageIcons: Record<string, typeof Layers> = {
    spaces: Layers,
    loads: Lightbulb,
    hardware: Cpu,
    controls: Gamepad2,
    products: Package,
    export: Download
  };

  const units = unitsQuery();
  const zones = zonesQuery();
  const rooms = roomsQuery();
  const loads = loadsQuery();
  const devices = inputDevicesQuery();
  const rcus = rcusQuery();
  const dinModules = dinModulesForUnitQuery();
  const rcuChannels = rcuChannelsQuery();
  const scenes = scenesQuery();
  const sceneLoads = unitSceneLoadsQuery();
  const products = productsQuery();

  let unit = $derived((units.data ?? []).find((u) => u.id === router.unitId) ?? null);
  let unitZones = $derived(zones.data ?? []);
  let unitRooms = $derived(rooms.data ?? []);
  let unitLoads = $derived(loads.data ?? []);
  let unitDevices = $derived(devices.data ?? []);
  let unitInputKeys = $derived((devices.data ?? []).flatMap((d) => d.keys ?? []));
  let unitChannels = $derived(
    (rcuChannels.data ?? []).map((c) => ({ load: c.load, object_id: c.rcu }))
  );

  function go(section: string) {
    onnavigate({ section });
  }

  let workflow = $derived(
    computeUnitWorkflow({
      unit: unit,
      zones: unitZones,
      rooms: unitRooms,
      loads: unitLoads,
      inputDevices: unitDevices,
      inputKeys: unitInputKeys,
      rcus: rcus.data ?? [],
      dinModules: dinModules.data ?? [],
      channels: unitChannels,
      scenes: scenes.data ?? [],
      sceneLoads: sceneLoads.data ?? [],
      products: products.data ?? []
    })
  );
  let stages = $derived(workflow.stages);
  let issueRows = $derived(workflow.issues);
  let completeCount = $derived(workflow.completeCount);
  let attentionCount = $derived(workflow.attentionCount);
  let nextAction = $derived(workflow.nextAction);
</script>

<section class="workflow-shell" aria-label="Unit workflow dashboard">
  <div class="workflow-header">
    <div>
      <p class="eyebrow">Design workflow</p>
      <h2>{completeCount} of {stages.length} stages complete</h2>
      <p class="muted">
        A compact view of the unit's physical design, controls, and export readiness.
      </p>
    </div>
    <button class="next-action" type="button" onclick={() => go(nextAction.section)}>
      <span>
        <strong>{nextAction.label}</strong>
        <small>{nextAction.message}</small>
      </span>
      <ArrowRight class="h-4 w-4" />
    </button>
  </div>

  <div class="stage-grid">
    {#each stages as stage}
      {@const StageIcon = stageIcons[stage.id]}
      <button
        class="stage-card"
        class:complete={stage.status === 'complete'}
        class:attention={stage.status === 'attention'}
        type="button"
        onclick={() => go(stage.section)}
      >
        <span class="stage-icon"><StageIcon class="h-4 w-4" /></span>
        <span class="stage-copy">
          <span class="stage-title">{stage.label}</span>
          <span class="stage-detail">{stage.detail}</span>
          {#if stage.issue}
            <span class="stage-issue">{stage.issue}</span>
          {/if}
        </span>
        <span class="stage-state">
          {#if stage.status === 'complete'}
            <CheckCircle2 class="h-4 w-4" />
          {:else if stage.status === 'attention'}
            <AlertCircle class="h-4 w-4" />
          {:else}
            <Circle class="h-4 w-4" />
          {/if}
        </span>
      </button>
    {/each}
  </div>

  <div class="attention-panel">
    <div class="panel-title">
      <Route class="h-4 w-4" />
      <span>Needs attention</span>
      <strong>{attentionCount}</strong>
    </div>

    {#if issueRows.length === 0}
      <div class="empty-state">
        <CheckCircle2 class="h-5 w-5" />
        <span>No blocking workflow issues detected from loaded data.</span>
      </div>
    {:else}
      <div class="issue-list">
        {#each issueRows as issue}
          <button class="issue-row" type="button" onclick={() => go(issue.section)}>
            <span>
              <strong>{issue.label}</strong>
              <small>{issue.message}</small>
            </span>
            <ArrowRight class="h-4 w-4" />
          </button>
        {/each}
      </div>
    {/if}
  </div>
</section>

<style>
  .workflow-shell {
    container-type: inline-size;
    display: grid;
    gap: 1rem;
  }

  .workflow-header {
    align-items: stretch;
    background: var(--bg-canvas);
    border: 1px solid var(--border-default);
    border-radius: 18px;
    display: grid;
    gap: 1rem;
    grid-template-columns: minmax(0, 1fr) minmax(260px, 360px);
    padding: 1.25rem;
  }

  .eyebrow {
    color: var(--text-muted);
    font-size: 0.72rem;
    font-weight: 700;
    letter-spacing: 0.12em;
    margin: 0 0 0.35rem;
    text-transform: uppercase;
  }

  h2 {
    color: var(--text-primary);
    font-size: clamp(1.3rem, 2vw, 1.85rem);
    font-weight: 700;
    letter-spacing: -0.035em;
    line-height: 1.1;
    margin: 0;
  }

  .muted {
    color: var(--text-muted);
    margin: 0.45rem 0 0;
  }

  .next-action {
    align-items: center;
    background: var(--text-primary);
    border: 0;
    border-radius: 14px;
    color: var(--bg-base);
    cursor: pointer;
    display: flex;
    justify-content: space-between;
    padding: 1rem;
    text-align: left;
    transition: opacity 180ms ease;
  }

  .next-action:hover {
    opacity: 0.9;
  }

  .next-action span,
  .issue-row span {
    display: grid;
    gap: 0.2rem;
  }

  .next-action strong,
  .issue-row strong {
    font-size: 0.9rem;
    font-weight: 700;
  }

  .next-action small {
    color: var(--bg-base);
    opacity: 0.72;
  }

  .stage-grid {
    display: grid;
    gap: 0.75rem;
    /* Auto-wrap based on available width rather than a fixed 6 columns, so the
       cards stay readable inside the (narrower-than-viewport) canvas column. */
    grid-template-columns: repeat(auto-fit, minmax(165px, 1fr));
  }

  .stage-card {
    align-items: start;
    background: var(--bg-canvas);
    border: 1px solid var(--border-default);
    border-radius: 16px;
    color: var(--text-primary);
    cursor: pointer;
    display: grid;
    gap: 0.65rem;
    grid-template-columns: auto minmax(0, 1fr) auto;
    min-height: 116px;
    padding: 0.95rem;
    text-align: left;
    transition:
      border-color 180ms ease,
      box-shadow 180ms ease,
      transform 180ms ease;
  }

  .stage-card:hover {
    border-color: var(--border-strong);
    box-shadow: 0 12px 30px rgba(16, 34, 58, 0.08);
    transform: translateY(-1px);
  }

  .stage-card.complete {
    border-color: rgb(var(--c-state-ok) / 0.35);
  }

  .stage-card.attention {
    border-color: rgb(var(--c-state-warn) / 0.35);
  }

  .stage-icon {
    align-items: center;
    background: var(--bg-hover);
    border: 1px solid var(--border-default);
    border-radius: 999px;
    display: inline-flex;
    height: 2rem;
    justify-content: center;
    width: 2rem;
  }

  .stage-copy {
    display: grid;
    gap: 0.2rem;
    min-width: 0;
  }

  .stage-title {
    font-size: 0.9rem;
    font-weight: 700;
  }

  .stage-detail {
    color: var(--text-muted);
    font-size: 0.78rem;
  }

  .stage-issue {
    color: var(--state-warn);
    font-size: 0.75rem;
    font-weight: 600;
    margin-top: 0.15rem;
  }

  .stage-state {
    color: var(--text-disabled);
    height: 1rem;
    width: 1rem;
  }

  .stage-card.complete .stage-state {
    color: var(--state-ok);
  }

  .stage-card.attention .stage-state {
    color: var(--state-warn);
  }

  .attention-panel {
    background: var(--bg-canvas);
    border: 1px solid var(--border-default);
    border-radius: 18px;
    overflow: hidden;
  }

  .panel-title {
    align-items: center;
    border-bottom: 1px solid var(--border-default);
    color: var(--text-primary);
    display: flex;
    gap: 0.5rem;
    padding: 0.9rem 1rem;
  }

  .panel-title span {
    font-weight: 700;
  }

  .panel-title strong {
    background: var(--bg-hover);
    border-radius: 999px;
    color: var(--text-secondary);
    font-size: 0.75rem;
    margin-left: auto;
    padding: 0.15rem 0.55rem;
  }

  .issue-list {
    display: grid;
  }

  .issue-row {
    align-items: center;
    background: var(--bg-canvas);
    border: 0;
    border-bottom: 1px solid var(--border-default);
    color: var(--text-primary);
    cursor: pointer;
    display: flex;
    justify-content: space-between;
    padding: 0.9rem 1rem;
    text-align: left;
  }

  .issue-row:hover {
    background: var(--bg-hover);
  }

  .issue-row small {
    color: var(--text-muted);
  }

  .empty-state {
    align-items: center;
    color: var(--state-ok);
    display: flex;
    gap: 0.5rem;
    padding: 1rem;
  }

  /* Container queries (keyed off the dashboard's own width, not the viewport)
     so the layout adapts to the canvas column between the rail and inspector. */
  @container (max-width: 640px) {
    .workflow-header {
      grid-template-columns: 1fr;
    }
  }

  @container (max-width: 360px) {
    .stage-grid {
      grid-template-columns: 1fr;
    }
  }
</style>
